<template>
  <div class="ant-otp-input" :class="{ 'ant-otp-input--error': status === 'error' }">
    <a-input
      v-for="(_, index) in length"
      :key="index"
      :ref="(el) => setInputRef(el, index)"
      :value="digits[index]"
      :disabled="disabled"
      :size="size"
      :status="status"
      :maxlength="1"
      inputmode="numeric"
      class="ant-otp-input__box"
      @update:value="(val: string) => onInput(index, val)"
      @keydown="(e: KeyboardEvent) => onKeydown(index, e)"
      @paste="onPaste"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  length?: number;
  disabled?: boolean;
  size?: SizeType;
  status?: "" | "error" | "warning";
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  length: 6,
  disabled: false,
  size: "middle",
  status: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "complete", value: string): void;
}>();

const digits = ref<string[]>(Array.from({ length: props.length }, () => ""));
const inputRefs = ref<any[]>([]);

const setInputRef = (el: any, index: number) => {
  if (el) inputRefs.value[index] = el;
};

const syncFromModel = (value: string) => {
  const chars = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, props.length)
    .split("");
  digits.value = Array.from({ length: props.length }, (_, i) => chars[i] || "");
};

watch(
  () => props.modelValue,
  (val) => {
    const current = digits.value.join("");
    if (val !== current) syncFromModel(val ?? "");
  },
  { immediate: true }
);

watch(
  () => props.length,
  () => {
    digits.value = Array.from({ length: props.length }, (_, i) => digits.value[i] || "");
    syncFromModel(props.modelValue ?? "");
  }
);

const emitValue = () => {
  const value = digits.value.join("");
  emit("update:modelValue", value);
  emit("change", value);
  if (value.length === props.length && digits.value.every(Boolean)) {
    emit("complete", value);
  }
};

const focusAt = async (index: number) => {
  await nextTick();
  const target = inputRefs.value[index];
  const inputEl = target?.$el?.querySelector?.("input") || target?.input || target?.$el;
  inputEl?.focus?.();
  inputEl?.select?.();
};

const onInput = (index: number, val: string) => {
  const digit = String(val).replace(/\D/g, "").slice(-1);
  digits.value[index] = digit;
  emitValue();
  if (digit && index < props.length - 1) {
    focusAt(index + 1);
  }
};

const onKeydown = (index: number, e: KeyboardEvent) => {
  if (e.key === "Backspace") {
    if (digits.value[index]) {
      digits.value[index] = "";
      emitValue();
    } else if (index > 0) {
      digits.value[index - 1] = "";
      emitValue();
      focusAt(index - 1);
    }
    e.preventDefault();
  }

  if (e.key === "ArrowLeft" && index > 0) {
    e.preventDefault();
    focusAt(index - 1);
  }

  if (e.key === "ArrowRight" && index < props.length - 1) {
    e.preventDefault();
    focusAt(index + 1);
  }
};

const onPaste = (e: ClipboardEvent) => {
  e.preventDefault();
  const text = e.clipboardData?.getData("text") ?? "";
  const chars = text.replace(/\D/g, "").slice(0, props.length).split("");
  digits.value = Array.from({ length: props.length }, (_, i) => chars[i] || "");
  emitValue();
  focusAt(Math.min(chars.length, props.length - 1));
};
</script>

<style scoped>
.ant-otp-input {
  display: inline-flex;
  gap: 8px;
}

.ant-otp-input__box {
  width: 40px !important;
  text-align: center;
}

.ant-otp-input__box :deep(input) {
  text-align: center;
  font-weight: 600;
  letter-spacing: 1px;
}
</style>

<!--
  Hướng dẫn sử dụng — OTP
  Global: <h-otp v-model="otp" :length="6" @complete="onDone" />
  Import: import { OtpInput } from '@/components/ant/input'

  Props: modelValue, length, disabled, size, status
  Events: update:modelValue, change, complete
  Docs: src/components/ant/input/README.md
-->
